import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled, { keyframes } from 'styled-components';
import { store } from 'redux-rest-reducer';
import { Separator, Text, Row, Button } from '@cuemath/comps';
import Humanize from 'humanize-plus';

import { closeModal } from 'actions/modal';
import { Header } from 'semantic-ui-react';
import { Close } from 'styled-icons/material/Close';
import Select from 'react-select';
import COLORS from 'constants/colors';

import { HeaderHead, Message } from 'modules/form';

import { patch as patchBulkEditData } from 'actions/curriculum-bulk-update';

import { FIELD_TYPES, bulkEditSheetFields } from 'constants/form';

import InputForm from './input-form';
import RadioForm from './radio-form';
import CriteriaForm from './criteria-form';
import SelectForm from './select-form';

const FormWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: ${({ close }) => (close ? `0.1` : `1`)};
  pointer-events: ${({ close }) => (close ? `none` : `all`)};
`;

const SelectInput = styled(Select)`
  width: 75%;
`;

const HeaderHeadWrapper = styled(HeaderHead)`
  padding: 0px 10px 10px 10px;
  margin-top: 10px;
  bottom: 7px;
  position: relative;
`;

const CloseIcon = styled(Close)`
  position: absolute;
  top: 10px;
  z-index: 2;
  right: 15px;
  width: 20px;
  height: 20px;
  float: right;
  cursor: pointer;
`;

const Field = styled.div`
  width: 65%;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: ${({ profileView }) => `${profileView ? '10px' : '12px'}`};
`;

const Label = styled.label`
  width: 25%;
  display: flex;
  justify-content: left;
`;

const BoldText = styled(Text)`
  font-size: 16px;
  font-weight: 700;
`;

const ButtonWrapper = styled.div`
  opacity: ${({ close }) => (close ? `0.1` : `1`)};
  pointer-events: ${({ close }) => (close ? `none` : `all`)};
`;

const ButtonStyle = styled.span`
  margin-left: 20px;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Loader = styled.div`
  border: 8px solid ${COLORS.CLOUD};
  border-radius: 50%;
  border-top: 8px solid ${COLORS.BOSTON_BLUE};
  width: 40px;
  height: 40px;
  animation: ${spin} 2s linear infinite;
`;

const RADIO_FILTER = {
  CW: 'INCLASS',
  HW: 'ATHOME',
  ANY: 'ANY',
  Yes: true,
  No: false,
};

const KEYS = {
  is_live: 'is_available',
  is_timed: 'is_timed',
  location: 'attempt_location',
  time: 'sheet_time',
  removed: 'removed',
};

class BulkEdit extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      closeModal: false,
      selectedValue: null,
      editSheetPatch: store.patch(),
      data: null,
      dropdownData: {},
      disabled: false,
      showConfirmation: false,
      submitDone: false,
      message: null,
      errorText: null,
      value: null,
      removeValue: null,
      btnDisabled: false,
    };
  }

  static getDerivedStateFromProps(props, state) {
    const { message, editSheetPatch, data } = state;
    const { bulkEditArr, searchObj, isSelectAllChecked, bulkUpdate, programs } = props;

    const newState = {};
    if (!data && !isSelectAllChecked) {
      newState.data = { node_ids: bulkEditArr.map(sheet => sheet.id) };
    }

    if (!data && isSelectAllChecked) {
      newState.data = { filters: searchObj };
    }

    const patchEditSheet = store.patch(bulkUpdate, editSheetPatch.id);

    if (patchEditSheet.current) {
      newState.btnDisabled = true;
    }

    if (patchEditSheet.resolved) {
      if (patchEditSheet.resolved.isPatched && !message) {
        newState.submitDone = true;
        newState.errorText = '';
        newState.message = `${
          isSelectAllChecked ? programs.data.total_results : bulkEditArr.length
        } ${Humanize.pluralize(bulkEditArr.length, 'Worksheet')} updated.`;
      } else if (patchEditSheet.resolved.__CODE__ === 400) {
        newState.submitDone = true;
        newState.message = '';
        newState.errorText = patchEditSheet.resolved.__ERROR__.message;
      }
    }
    return newState;
  }

  componentDidUpdate() {
    const { submitDone } = this.state;
    if (submitDone) {
      this.timeout = setTimeout(() => {
        this.onCloseClicked();
      }, 3000);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  renderFormHeader = () => {
    const { closeModal } = this.state;

    return (
      <HeaderHeadWrapper close={closeModal}>
        <Header as="h3" floated="left">
          <CloseIcon onClick={this.onCloseClicked} />
          <Separator height={10} transparent />
          {
            <Text weight="bold" color="rgba(0,0,0,.87)">
              Bulk Updates
            </Text>
          }
        </Header>
      </HeaderHeadWrapper>
    );
  };

  onCloseClicked = () => {
    const { actions } = this.props;
    actions.closeModal();
  };

  onInputChange = (event, key) => {
    let tempData = Object.assign({}, this.state.data);
    if (key === 'time') {
      key = KEYS.time;
      tempData[`${key}`] = event * 60;
    } else {
      tempData[`${key}`] = event;
    }
    this.setState({ data: tempData, value: event });
  };

  handleRadioButtonChange = (e, key) => {
    const { name } = e.target;
    let tempData = Object.assign({}, this.state.data);
    tempData[KEYS[name]] = RADIO_FILTER[key];
    this.setState({ data: tempData, value: key });
  };

  onSelectChange = (key, value) => {
    const { data } = this.state;
    let tempData = { ...data };
    tempData[`${key}`] = value;

    if (key.includes(KEYS.removed)) {
      this.setState({ data: tempData, removeValue: value });
    } else {
      this.setState({ data: tempData, value: value });
    }
  };

  onTextChange = (event, { name, key, value }) => {
    const newData = Object.assign({}, this.state.data);
    const isOperatorKeyPresent = newData[`${name}`];
    if (isOperatorKeyPresent) {
      newData[`${name}`] = { ...newData[`${name}`], value: value };
      this.setState({
        value: value,
        data: newData,
      });
    } else {
      newData[`${name}`] = { ...newData[`${name}`], operator: '>=', value: value };
      this.setState({
        value: value,
        data: newData,
      });
    }
  };

  handleChangeDropdown = (event, { name, key, value }) => {
    let tempData = Object.assign({}, this.state.dropdownData);
    tempData[`criteria`] = value;

    const newData = Object.assign({}, this.state.data);
    newData[name] = { ...newData[name], operator: value };
    this.setState({
      dropdownData: tempData,
      data: newData,
    });
  };

  getCheckOptions = (options, isIdTrue) => {
    let arr = [];

    if (isIdTrue) {
      arr = options.map(opt => {
        return { label: opt.name, value: opt.id };
      });
    } else {
      arr = options.map(opt => {
        return { label: opt.name, value: opt.code };
      });
    }

    return arr;
  };

  onChange = selected => {
    this.setState({
      selectedValue: selected,
      data: null,
      value: '',
      dropdownData: {},
      removeValue: null,
    });
  };

  onUpdate = () => {
    const { data } = this.state;
    let temp = data;
    for (let key in temp) {
      if (temp[key] == null) {
        delete temp[key];
      }
    }
    this.setState({ showConfirmation: true, disabled: true, data: temp });
  };

  onCancel = () => {
    this.setState({ showConfirmation: false, disabled: false });
  };

  onConfirm = () => {
    const { actions } = this.props;
    const {
      data,
      editSheetPatch: { id: patchId },
    } = this.state;

    let patch_data;
    if (data) {
      if (data.node_ids) {
        patch_data = data;
      } else {
        let { filters, ...params } = data;
        let { page, per_page, search, ...newFilters } = filters;
        if (search) {
          patch_data = { search, filters: newFilters, ...params };
        } else {
          patch_data = { filters: newFilters, ...params };
        }
      }
    }

    const patchObj = {
      action: 'BULK_UPDATE',
      action_data: patch_data,
    };

    actions.patchBulkEditData(patchId, 'patchid', patchObj);
  };

  renderUpdateOptions() {
    const { selectedValue, value, disabled, dropdownData, removeValue } = this.state;
    const { tag } = this.props;
    switch (selectedValue.fieldType) {
      case FIELD_TYPES.SELECT:
        return (
          <SelectForm
            selectedValue={selectedValue}
            value={value}
            removeValue={removeValue}
            disabled={disabled}
            onSelectChange={this.onSelectChange}
            tag={tag}
          />
        );
      case FIELD_TYPES.INPUT:
        return (
          <InputForm
            selectedValue={selectedValue}
            value={value}
            disabled={disabled}
            onInputChange={this.onInputChange}
          />
        );
      case FIELD_TYPES.CRITERIA:
        return (
          <CriteriaForm
            selectedValue={selectedValue}
            value={value}
            disabled={disabled}
            handleChangeDropdown={this.handleChangeDropdown}
            onTextChange={this.onTextChange}
            dropdownData={dropdownData}
          />
        );
      case FIELD_TYPES.RADIO:
        return (
          <RadioForm
            selectedValue={selectedValue}
            value={value}
            disabled={disabled}
            handleRadioButtonChange={this.handleRadioButtonChange}
          />
        );
      default:
        return null;
    }
  }

  disableAddSheet = () => {
    const { data } = this.state;
    if (!data || Object.keys(data).length < 2) return true;
    const {
      node_type,
      added_board_ids,
      removed_board_ids,
      added_grade_ids,
      removed_grade_ids,
      added_topic_ids,
      removed_topic_ids,
      complete_in,
      sheet_time,
      reattempt_in,
      complete_criteria,
      reattempt_criteria,
    } = data;
    if (
      (node_type && node_type !== '') ||
      (added_board_ids && added_board_ids.length) ||
      (removed_board_ids && removed_board_ids.length) ||
      (added_grade_ids && added_grade_ids.length) ||
      (removed_grade_ids && removed_grade_ids.length) ||
      (added_topic_ids && added_topic_ids.length) ||
      (removed_topic_ids && removed_topic_ids.length) ||
      (complete_in && complete_in !== '') ||
      (sheet_time && sheet_time !== '') ||
      (reattempt_in && reattempt_in !== '') ||
      Object.keys(data).includes(KEYS.is_live) ||
      Object.keys(data).includes(KEYS.is_timed) ||
      Object.keys(data).includes(KEYS.location) ||
      (complete_criteria && complete_criteria.value !== '') ||
      (reattempt_criteria && reattempt_criteria.value !== '')
    ) {
      return false;
    }
    return true;
  };

  render() {
    const { bulkEditArr, programs, isSelectAllChecked } = this.props;
    const isButtonDisabled = this.disableAddSheet();
    const {
      selectedValue,
      disabled,
      showConfirmation,
      message,
      btnDisabled,
      errorText,
    } = this.state;
    return (
      <FormWrapper>
        {this.renderFormHeader()}
        <Separator height={20} transparent />
        <BoldText>
          {isSelectAllChecked ? programs.data.total_results : bulkEditArr.length}{' '}
          {Humanize.pluralize(bulkEditArr.length, 'sheet')} selected
        </BoldText>
        <Separator height={30} transparent />
        <Field justifyContent>
          <Label>Tag</Label>
          <SelectInput
            onChange={select => this.onChange(select)}
            isSearchable
            isDisabled={disabled}
            options={bulkEditSheetFields}
          />
        </Field>
        <Separator height={20} transparent />
        {selectedValue ? this.renderUpdateOptions() : null}
        <Separator height={30} transparent />
        {message && !errorText && <Message>{message}</Message>}
        {!message && errorText && <Message>{errorText}</Message>}
        {!showConfirmation && !message && !errorText && (
          <Button
            onClick={this.onUpdate}
            disabled={isButtonDisabled}
            label={'Update'}
          ></Button>
        )}

        {showConfirmation && !message && !errorText && (
          <ButtonWrapper close={false}>
            <Text>Are you sure you want to make this change?</Text>
            <Separator height="20" transparent />
            <Row justifyContent="center">
              <Button disabled={btnDisabled} label="Confirm" onClick={this.onConfirm} />
              <ButtonStyle>
                <Button disabled={btnDisabled} label="Cancel" onClick={this.onCancel} />
              </ButtonStyle>
            </Row>
          </ButtonWrapper>
        )}
        {btnDisabled && !message && !errorText && <Loader />}
      </FormWrapper>
    );
  }
}

export default connect(
  ({ bulkUpdate }) => ({
    bulkUpdate,
  }),
  dispatch => ({
    actions: bindActionCreators({ closeModal, patchBulkEditData }, dispatch),
  }),
)(BulkEdit);
